import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import i18n from 'i18n-js';
import moment from 'moment';
import { useHover } from 'react-native-web-hooks';

import { scale, Element, is24HourFormat, width } from '@components/frame';
import { ncFileStatuses, isWeb, formatSinceTimeWithDayTranslationCase } from '@helpers';

import ColorWrapper, { checkColor } from '@styles/colors';
import { font } from '@styles/fonts';
import Svg from '@styles/icons/svg';

import Image from '@components/common/ImageOverlay';
import TouchableElement from '@components/common/touchableElement';
import ItemHistoryTime from '@components/common/ItemHistoryTime';
import ItemOEETime from '@v2/pages/machines/modules/performance/components/PerformanceUtilization/ItemOEETime';
import { KEYS_TAGS } from '@components/imageHandling/common';

import { SmallLoading } from '@components/loading';

import NcFileIcon from './ncFileIcon';

import {
  machineStatus,
  subStatusName,
  subStatuses,
  convertTime,
  isPhone,
  machineStatusColorName,
  ImageUpload,
} from '../../../helpers';

import ItemTime from './detailsComponents/activeMachine/components/TimeLine';
import { MachinesScreen } from '@v2/pages/machines/common/enums';
import { useRoute } from '@react-navigation/native';
import { connect } from 'react-redux';
import { setSelectedMachine } from '@reducers/machines';
import { useForceUpdateEverySecond } from '@v2/pages/common/hooks/useForceUpdateEverySecond';
import { IMAGE_UPLOAD_HEADER_TEXT } from '@v2/pages/common/constants/base.constants';
import { useNavigationCheck } from '@v2/pages/machines/hooks/useNavigationCheck';

const ItemName = React.memo(
  ({ shortName, C, selected, isOperated, status, isProcessingPI, loading, displayTime }) => (
    <View style={{ flexDirection: 'row', alignContent: 'center', alignItems: 'center', marginTop: scale(-2), paddingRight: scale(isPhone ? 35 : 0) }}>
      {displayTime && (
        <View
          style={[
            {
              width: scale(10),
              height: scale(10),
              borderRadius: scale(10),
              backgroundColor: C(machineStatusColorName[status]),
              opacity: isProcessingPI || loading ? 0 : 1,
            },
          ]}
        />
      )}
      <Text
        style={[{ marginLeft: scale(5.5), color: C(selected ? 'Label/Light/AlwaysLight Primary' : 'Label / Light / Primary') }, font.Headline]}
        numberOfLines={1}
      >
        {shortName}
      </Text>
      {isOperated && (
        <Element
          {...{
            config: {
              top: 5,
              right: 2,
            },
          }}
        >
          {selected ? (
            <Svg icon="operated" key="operatedSelected" color={'Label/Light/AlwaysLight Secondary'} width={scale(12)} height={scale(12)} />
          ) : (
            <Svg icon="operated" key="operatedNotSelected" color={'Label/Light/Tertiary'} width={scale(12)} height={scale(12)} />
          )}
        </Element>
      )}
    </View>
  ),
  (prev, next) =>
    prev.status === next.status &&
    prev.shortName === next.shortName &&
    prev.C.scheme === next.C.scheme &&
    prev.selected === next.selected &&
    prev.isProcessingPI === next.isProcessingPI &&
    prev.loading === next.loading &&
    prev.displayTime === next.displayTime &&
    prev.isOperated === next.isOperated
);

const RenderImage = React.memo(
  ({ uploadedImage, imageLink, imageRandomHash, rotationAngle, C, borderRadius }) => {
    return (
      <Image
        C={C}
        rotate={rotationAngle}
        source={uploadedImage ? uploadedImage : { uri: imageLink + `&dummy=${imageRandomHash}` || ' ' }}
        resizeMode="contain"
        style={{ width: '100%', height: '100%' }}
        borderRadius={borderRadius}
      />
    );
  },
  (prev, next) => {
    return prev.imageLink === next.imageLink && prev.imageRandomHash === next.imageRandomHash && prev.rotationAngle === next.rotationAngle && prev.borderRadius === next.borderRadius;
  }
);

export const ItemImg = React.memo(
  ({
    C,
    ncFile,
    ncFileId,
    imageHash,
    imageData = {},
    imageDetails = [],
    selected,
    isProcessing,
    imageRandomHash,
    generateImageURL,
    loading: globalLoading,
    subStatus,
    shortName,
    machineId,
  }) => {
    const { safeIsNowSubScreenCheck } = useNavigationCheck();

    const [loading, setLoading] = useState(false);
    const [uploadedImage, setUploadedImage] = useState(null);
    const [imageLink, setImageLink] = useState(null);
    const details = imageDetails?.find((i) => imageData?.pageNumber === i.pageNumber);
    useEffect(() => {
      if (imageData?.local && imageData?.url) {
        setImageLink(imageData.url);
        return;
      }
      (async () => {
        if (imageHash && imageData) {
          const imageLink = await generateImageURL({
            size: '_1x',
            cropped: imageData?.isCropped,
            item: { imageHash, extensionDerivative: imageData?.extensionDerivative },
            page: imageData?.pageNumber,
          });
          setImageLink(imageLink);
        }
      })();
    }, [imageHash]);

    const possibleAddImage = ncFileId && !imageHash && !uploadedImage && !isProcessing;

    const uploadImageFunction = () => {
      if (loading || !possibleAddImage) {
        return;
      }

      ImageUpload({
        headerText: IMAGE_UPLOAD_HEADER_TEXT.AddToMachine,
        headerDescription: shortName,
        ...(safeIsNowSubScreenCheck ? { ncFile } : {}),
        uploadStart: () => setLoading(true),
        uploadEnd: (img) => {
          setLoading(false);
          setUploadedImage(img);
        },
        tags: [
          {
            scopeId: safeIsNowSubScreenCheck ? KEYS_TAGS['Clamping'] : KEYS_TAGS.Machine,
            scopeReferenceId: safeIsNowSubScreenCheck ? ncFileId : machineId,
            isFavorite: true,
          },
        ],
      });
    };

    const isOffline = subStatus === subStatuses.NotPingable && !globalLoading;

    return (
      <TouchableElement onPress={possibleAddImage ? uploadImageFunction : null}>
        {({ boxStyle }) => (
          <View
            style={[
              possibleAddImage ? boxStyle : {},
              {
                width: scale(90),
                height: scale(60),
                position: 'relative',
                justifyContent: 'center',
                alignContent: 'center',
                alignItems: 'center',
                opacity: isOffline ? 0 : 1,
              },
            ]}
          >
            <View
              style={{
                width: scale(90),
                height: scale(60),
                backgroundColor: C('Overlay/Light/Dim very light'),
                opacity: 0.5,
                position: 'absolute',
              }}
            />
            {isProcessing && (
              <View
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  zIndex: 10000,
                  backgroundColor: C('Background/Light/System'),
                }}
              >
                <View
                  style={{
                    backgroundColor: C('Picture/Light/Overlay'),
                    justifyContent: 'center',
                    alignContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <Svg icon={'pdf'} width={scale(23)} height={scale(29)} color="Label/Light/Quaternary" />
                  <Text style={[font.Footnote, { color: C('Label/Light/Quaternary') }]}>{i18n.t('Processing...')}</Text>
                </View>
              </View>
            )}
            {globalLoading && (
              <View
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  zIndex: 10000,
                  backgroundColor: C('Background/Light/System'),
                }}
              >
                <View
                  style={{
                    backgroundColor: C('Picture/Light/Overlay'),
                    justifyContent: 'center',
                    alignContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                  }}
                />
              </View>
            )}
            {false ? (
              <SmallLoading />
            ) : possibleAddImage ? (
              <TouchableElement onPress={uploadImageFunction}>
                {({ boxStyle, hovered }) => (
                  <View
                    style={[
                      boxStyle,
                      {
                        backgroundColor: hovered ? C(selected ? 'Button/Light/Secondary hover' : 'Button/Light/Secondary hover') : 'transparent',
                        borderRadius: scale(4),
                        padding: scale(8),
                      },
                    ]}
                  >
                    <Svg
                      icon="addPicture"
                      color={selected ? (hovered ? 'Button/Light/Primary Text' : 'Label/Light/Always Light Tertiary') : 'Button/Light/Secondary Text'}
                      width={scale(45)}
                      height={scale(34)}
                      key={'image-icon-' + selected}
                    />
                  </View>
                )}
              </TouchableElement>
            ) : (
              <RenderImage {...{ uploadedImage, imageLink, imageRandomHash, rotationAngle: imageData?.isCropped ? 0 : details?.rotationAngle, C, borderRadius: scale(8) }} />
            )}
            {!possibleAddImage && (
              <View
                style={{
                  width: scale(90),
                  height: scale(60),
                  backgroundColor: C('Overlay/Light/Dim light'),
                  opacity: 0.5,
                  borderRadius: scale(8),
                  position: 'absolute',
                }}
              />
            )}
          </View>
        )}
      </TouchableElement>
    );
  },
  (prev, next) =>
    prev.imageHash === next.imageHash &&
    prev.subStatus === next.subStatus &&
    prev.loading === next.loading &&
    prev.imageData === next.imageData &&
    prev.imageRandomHash === next.imageRandomHash &&
    prev.C.scheme === next.C.scheme
);

const NotProductiveBlock = React.memo(
  ({ timeOffset, fullWidth, ncFileStatus, subStatus, ncFile, selected, lastModified, C, status }) => {
    const color = C(selected ? 'Label/Light/AlwaysLight Secondary' : 'Label/Light/Tertiary');

    useForceUpdateEverySecond();

    const time = moment.utc().add(timeOffset, 'milliseconds');

    const lastModifiedMilliseconds = moment.utc(lastModified).valueOf();

    const lastTime = Math.round((time.utc().valueOf() - lastModifiedMilliseconds) / 1000);

    const renderNcFileText = () => {
      if (ncFileStatus === ncFileStatuses.Unlinked) {
        return i18n.t('Production Information Missing');
      }

      return ncFile;
    };

    const renderInfo = () => {
      if (subStatus === subStatuses.MachineConnectorOff || subStatus === subStatuses.NotPingable || !ncFile) {
        return (
          <View>
            <View style={{ flexDirection: 'row', alignItems: 'center', alignContent: 'center', marginTop: scale(1) }}>
              <View style={{ width: scale(10), height: scale(12), justifyContent: 'center' }}>
                <NcFileIcon {...{ status: ncFileStatus, machineStatus: status, subStatus, ncFile, C, selected }} />
              </View>
              <View style={{ marginLeft: scale(5.5), flex: 1 }}>
                <Text style={[{ color }, font['Subhead 1']]}>{i18n.t(subStatusName[subStatus] || subStatus)}</Text>
              </View>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', alignContent: 'center' }}>
              <View style={{ width: scale(10), height: scale(10) }} />
              <View style={{ marginLeft: scale(5.5), flex: 1 }}>
                <Text style={[{ color }, font['Subhead 1']]} numberOfLines={1}>
                  {formatSinceTimeWithDayTranslationCase(lastModified, is24HourFormat)}
                </Text>
              </View>
            </View>
          </View>
        );
      }

      if (ncFile) {
        return (
          <View>
            <View style={{ flexDirection: 'row', alignItems: 'center', alignContent: 'center', marginTop: scale(1) }}>
              <View style={{ width: scale(10), height: scale(12), justifyContent: 'center' }}>
                <NcFileIcon {...{ status: ncFileStatus, ncFile, machineStatus: status, subStatus, C, selected }} />
              </View>
              <View style={{ marginLeft: scale(5.5), flex: 1 }}>
                <Text style={[{ color }, font['Subhead 1']]} numberOfLines={1} ellipsizeMode="middle">
                  {renderNcFileText()}
                </Text>
              </View>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', alignContent: 'center' }}>
              <View style={{ width: scale(10), height: scale(10) }} />
              <View style={{ marginLeft: scale(5.5), flex: 1 }}>
                <Text style={[{ color, fontVariant: ['tabular-nums'] }, font['Subhead 1']]} numberOfLines={1}>
                  {`${i18n.t(subStatusName[subStatus] || subStatus)} ${formatSinceTimeWithDayTranslationCase(lastModified, is24HourFormat)}`}
                </Text>
              </View>
            </View>
          </View>
        );
      }
    };
    return <View style={{ flex: 1 }}>{renderInfo()}</View>;
  },
  (prev, next) =>
    prev.timeOffset === next.timeOffset &&
    prev.ncFileStatus === next.ncFileStatus &&
    prev.subStatus === next.subStatus &&
    prev.ncFile === next.ncFile &&
    prev.selected === next.selected &&
    prev.lastModified === next.lastModified &&
    prev.C.scheme === next.C.scheme
);

const Item = ({
  item,
  setSelectedMachine,
  timeOffset,
  selected: s,
  C,
  nextSelected,
  updateAppValue,
  generateImageURL,
  loading,
  displayTime,
  listWidth,
  roundTop,
  roundBottom,
}) => {
  const route = useRoute();
  const ref = useRef(null);
  const [pressed, setPressed] = useState(false);
  const hovered = useHover(ref);

  const pressItem = (item) => {
    setSelectedMachine(item);
  };

  const fullWidth = isPhone ? width - scale(167) : 197;

  const selected = s && !isPhone;
  const displayOverlay = selected || pressed || hovered;

  const pressHoverColor = (c) => {
    if (pressed) {
      return checkColor(c, 'pressed');
    }
    if (hovered) {
      return checkColor(c, 'hover');
    }
    return c;
  };

  return (
    <TouchableOpacity
      style={[
        {
          paddingLeft: scale(isPhone ? 18 : displayOverlay ? 12 : 22),
          paddingRight: scale(isPhone ? 0 : displayOverlay ? 12 : 22),
          width: '100%',
          height: scale(80),
          position: 'relative',
          zIndex: 100000,
        },
        !isWeb && {
          backgroundColor: C(pressHoverColor('Background/Light/Field')),
        },
        roundTop && {
          borderTopLeftRadius: scale(10),
          borderTopRightRadius: scale(10),
        },
        roundBottom && {
          borderBottomLeftRadius: scale(10),
          borderBottomRightRadius: scale(10),
        },
      ]}
      onPress={() => {
        if (item.statisticOnly) {
          return;
        }
        pressItem(item);
      }}
      activeOpacity={1}
      ref={ref}
      onPressIn={() => {
        setPressed(true);
      }}
      onPressOut={() => {
        setPressed(false);
      }}
    >
      <View
        style={[
          {
            width: '100%',
            height: '100%',
            borderRadius: 0,
            paddingTop: scale(10),
            flexDirection: 'row',
            overflow: 'hidden',
          },
          selected && {
            backgroundColor: C('Selected/Light/List item'),
            borderBottomWidth: 0,
            borderRadius: scale(18),
            paddingLeft: scale(10),
            paddingRight: scale(10),
          },
          (pressed || hovered) &&
            !selected &&
            !isPhone && {
              backgroundColor: C(pressHoverColor('Background/Light/Field')),
              borderRadius: scale(18),
              paddingLeft: scale(isPhone ? 0 : 10),
              paddingRight: scale(isPhone ? 0 : 10),
            },
        ]}
      >
        <ItemImg
          {...{
            C,
            imageData: item.machineStatus.imageData,
            imageDetails: item.machineStatus.imageDetails,
            subStatus: item.machineStatus.subStatus,
            shortName: item.shortName,
            ncFile: item.machineStatus.ncFile,
            imageHash: item.machineStatus.imageHash,
            ncFileId: item.machineStatus.ncFileId,
            imageRandomHash: item.machineStatus.imageRandomHash,
            isProcessing: item.isProcessing,
            machineId: item?.machineId,
            generateImageURL,
            selected,
            loading,
          }}
        />
        <View
          style={{
            height: '100%',
            marginLeft: scale(5.5, false),
            flex: 1,
            borderBottomWidth: (selected || nextSelected) && !isPhone ? 0 : StyleSheet.hairlineWidth,
            borderBottomColor: !roundBottom ? C('Hairline/Light/No trans') : 'transparent',
            paddingBottom: scale(10),
          }}
        >
          <ItemName
            {...{
              isOperated: item.machineStatus.isOperated,
              status: item.machineStatus.status,
              shortName: item.shortName,
              isProcessingPI: item.isProcessingPI,
              C,
              selected,
              loading,
              displayTime,
            }}
          />
          {displayTime || route.name.includes(MachinesScreen.TIMELINE) ? (
            loading ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', alignContent: 'center', marginTop: scale(1) }}>
                <View style={{ width: scale(10), height: scale(12), justifyContent: 'center' }}></View>
                <View style={{ marginLeft: scale(5.5), flex: 1 }}>
                  <Text style={[font['Subhead 2'], { color: C(selected ? 'Label/Light/AlwaysLight Secondary' : 'Label/Light/Tertiary') }]}>
                    {i18n.t('Connecting')}...
                  </Text>
                </View>
              </View>
            ) : item.machineStatus.status !== machineStatus.NOTPRODUCTIVE ? (
              <View style={{ marginTop: scale(9), marginLeft: scale(15.5), flex: 1, paddingRight: scale(10), opacity: item.isProcessingPI ? 0 : 1 }}>
                <ItemTime
                  {...{
                    timeOffset,
                    C,
                    machineStatus: item.machineStatus,
                    updateAppValue,
                    hideNCFile: true,
                    hideDurationTime: true,
                    selected,
                  }}
                />
              </View>
            ) : (
              <NotProductiveBlock
                {...{
                  fullWidth,
                  ncFileStatus: item.machineStatus.ncFileStatus,
                  ncFile: item.machineStatus.ncFile,
                  subStatus: item.machineStatus.subStatus,
                  status: item.machineStatus.status,
                  lastModified: item.machineStatus.lastUpdatedOn,
                  C,
                  selected,
                  timeOffset,
                }}
              />
            )
          ) : route.name.includes(MachinesScreen.UTILIZATION) ? (
            <ItemOEETime
              {...{
                item,
                C,
                selected,
                listWidth,
              }}
            />
          ) : (
            <ItemHistoryTime
              {...{
                item,
                C,
                selected,
                listWidth,
              }}
            />
          )}
          {!selected && (
            <Element
              {...{
                config: {
                  top: '50%',
                  right: isPhone ? 18 : 0,
                  transform: [{ translateY: -7 }],
                },
              }}
            >
              <Svg icon="chevronRightLight" color={'Label/Light/Quinary'} width={scale(7)} height={scale(13)} />
            </Element>
          )}
        </View>
      </View>
    </TouchableOpacity>
  );
};

const compareProps = (prev, next) => {
  return (
    prev.item.machineStatus.status === next.item.machineStatus.status &&
    prev.item.machineStatus.subStatus === next.item.machineStatus.subStatus &&
    prev.item.machineStatus.lastUpdatedOn === next.item.machineStatus.lastUpdatedOn &&
    prev.item.machineStatus.ncFile === next.item.machineStatus.ncFile &&
    prev.item.machineStatus.imageRandomHash === next.item.machineStatus.imageRandomHash &&
    prev.item.machineStatus.imageHash === next.item.machineStatus.imageHash &&
    prev.item.machineStatistics?.totalLoaded === next.item.machineStatistics?.totalLoaded &&
    prev.item.shortName === next.item.shortName &&
    prev.item.isProcessing === next.item.isProcessing &&
    prev.item.isProcessingPI === next.item.isProcessingPI &&
    prev.C.scheme === next.C.scheme &&
    prev.timeOffset === next.timeOffset &&
    prev.selected === next.selected &&
    prev.updateAppValue === next.updateAppValue &&
    prev.nextSelected === next.nextSelected &&
    prev.displayTime === next.displayTime &&
    prev.loading === next.loading &&
    prev.item?.machineId === next.item?.machineId
  );
};

export default connect(null, { setSelectedMachine })(ColorWrapper(React.memo(Item, compareProps)));
